import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { JobType } from "../types/job/job";
import { JobCategoryProps } from "../types/job/jobCategory";
import { JobRoleProps } from "../types/job/jobRole";
import { api } from "../services/api.service";

interface FormData {
  name?: string;
  jobCategory?: string;
  jobType?: string;
  experience?: string;
  sortBy?: string;
}

interface JobState {
  jobs: JobType[];
  job: JobType | null;
  jobCategories: JobCategoryProps[];
  noOfJobs: number;
  noOfActiveJobs: number;
  noOfPendingJobs: number;
  noOfExpiredJobs: number;
  jobRoles: JobRoleProps[];
  loading: boolean;
  error: string | null;
}

const initialState: JobState = {
  jobs: [],
  job: null,
  jobCategories: [],
  jobRoles: [],
  noOfJobs: 0,
  noOfActiveJobs: 0,
  noOfPendingJobs: 0,
  noOfExpiredJobs: 0,
  loading: false,
  error: null,
};

export const fetchJobs = createAsyncThunk<JobType[], FormData>(
  "job/fetchjobs",
  async ({ name, jobCategory, jobType, experience, sortBy }) => {
    const params: any = {};
    if (name) params.title = name; // Updated to match backend
    if (jobCategory) params.job_type = jobCategory; // Updated to match backend
    if (jobType) params.job_type = jobType; // Updated to match backend
    if (experience) params.experience = experience;
    if (sortBy) params.sort = sortBy;

    const response = await api.job.getAll(params);
    // Handle the new response format from backend
    return response.data?.items || response.data || response as JobType[];
  }
);
export const fetchJob = createAsyncThunk<JobType, { id: number }>(
  "job/fetchjob",
  async ({ id }) => {
    const response = await api.job.getById(id);
    // Handle the new response format from backend
    return response.data || response as JobType;
  }
);

export const fetchJobCategories = createAsyncThunk<JobCategoryProps[]>(
  "job/fetchjobcategories",
  async () => {
    const data = await api.jobCategory.getAll();
    return data as JobCategoryProps[];
  }
);

export const fetchJobRoles = createAsyncThunk<JobRoleProps[]>("job/fetchjobroles", async () => {
  const data = await api.jobRole.getAll();
  return data as JobRoleProps[];
});
export const fetchNoOfJobs = createAsyncThunk<number>("job/fetchNoOfJobs", async () => {
  const response = await api.job.count();
  return response.data?.total || response.total || response as number;
});
export const fetchNoOfPendingJobs = createAsyncThunk<number>(
  "job/fetchNoOfPendingJobs",
  async () => {
    const response = await api.job.countPending();
    return response.data?.pending || response.pending || response as number;
  }
);
export const fetchNoOfActiveJobs = createAsyncThunk<number>(
  "job/fetchNoOfActiveJobs",
  async () => {
    const response = await api.job.countActive();
    return response.data?.active || response.active || response as number;
  }
);
export const fetchNoOfExpiredJobs = createAsyncThunk<number>(
  "job/fetchNoOfExpiredJobs",
  async () => {
    const response = await api.job.countExpired();
    return response.data?.expired || response.expired || response as number;
  }
);

export const jobSlice = createSlice({
  name: "job",
  initialState,
  reducers: {
    addJob: (state, action: PayloadAction<JobType>) => {
      state.jobs.push(action.payload);
    },
    removeJob: (state, action: PayloadAction<JobType>) => {
      state.jobs = state.jobs.filter((job) => job.id !== action.payload.id);
    },
    updateJob: (state, action: PayloadAction<JobType>) => {
      state.jobs = state.jobs.map((job) =>
        job.id === action.payload.id ? action.payload : job
      );
    },
    addJobCategory: (state, action: PayloadAction<JobCategoryProps>) => {
      state.jobCategories.push(action.payload);
    },
    removeJobCategory: (state, action: PayloadAction<JobCategoryProps>) => {
      state.jobCategories = state.jobCategories.filter(
        (jobCategory) => jobCategory.id !== action.payload.id
      );
    },
    updateJobCategory: (state, action: PayloadAction<JobCategoryProps>) => {
      state.jobCategories = state.jobCategories.map((jobCategory) =>
        jobCategory.id === action.payload.id ? action.payload : jobCategory
      );
    },
    addJobRole: (state, action: PayloadAction<JobRoleProps>) => {
      state.jobRoles.push(action.payload);
    },
    removeJobRole: (state, action: PayloadAction<JobRoleProps>) => {
      state.jobRoles = state.jobRoles.filter(
        (jobRole) => jobRole.id !== action.payload.id
      );
    },
    updateJobRole: (state, action: PayloadAction<JobRoleProps>) => {
      state.jobRoles = state.jobRoles.map((jobRole) =>
        jobRole.id === action.payload.id ? action.payload : jobRole
      );
    },
  },

  extraReducers(builder) {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.jobs = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJob.fulfilled, (state, action) => {
        state.job = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchJob.rejected, (state, action) => {
        state.error = action.error.message!;
        state.loading = false;
      })
      .addCase(fetchJobCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobCategories.fulfilled, (state, action) => {
        state.jobCategories = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchJobCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchJobRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobRoles.fulfilled, (state, action) => {
        state.jobRoles = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchJobRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchNoOfJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNoOfJobs.fulfilled, (state, action) => {
        state.noOfJobs = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNoOfJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchNoOfActiveJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNoOfActiveJobs.fulfilled, (state, action) => {
        state.noOfActiveJobs = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNoOfActiveJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchNoOfPendingJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNoOfPendingJobs.fulfilled, (state, action) => {
        state.noOfPendingJobs = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNoOfPendingJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      })
      .addCase(fetchNoOfExpiredJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNoOfExpiredJobs.fulfilled, (state, action) => {
        state.noOfExpiredJobs = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchNoOfExpiredJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message!;
      });
  },
});

export const {
  addJob,
  removeJob,
  updateJob,
  addJobCategory,
  removeJobCategory,
  updateJobCategory,
  addJobRole,
  removeJobRole,
  updateJobRole,
} = jobSlice.actions;

export default jobSlice.reducer;
